/**
 * resumeBurst — mede RAJADA de retomadas e a transforma em UM relato (BUG-243).
 *
 * ── Por que isto existe ─────────────────────────────────────────────────────
 * Medido em 13–14/09 (log de requests): um Galaxy Z Flip disparou a sonda de
 * retomada ~6×/min por 2 h seguidas, sem nenhum reload — o piso de 10 s do
 * `FirestoreResumeWatcher` era o que ritmava. Algo dispara `visibilitychange`
 * /`appStateChange` em intervalo menor que 10 s nesse aparelho, e a causa não
 * foi medida porque o `resumeTrace` é local (vira print, e ninguém tinha o
 * aparelho). Este módulo é o dado que falta, sem esperar alguém emprestar um
 * Z Flip: quando a rajada acontece em QUALQUER aparelho, sai um relato com o
 * que distingue as hipóteses do bug —
 *   · QUAL fonte dispara (`visibilitychange` × `appStateChange`);
 *   · quanto tempo a página ficou ESCONDIDA antes de cada volta — milissegundos
 *     apontam para troca de tela do dobrável/multi-janela; segundos, para a
 *     pessoa entrando e saindo;
 *   · o user-agent (o `releaseId` e a rota já vão no `captureException`).
 *
 * ── Decisões ────────────────────────────────────────────────────────────────
 * 1. **Puro, com o relógio por argumento.** O watcher só alimenta; a regra de
 *    "é rajada?" é testável sem timers falsos.
 * 2. **Um relato por rajada, e rajadas espaçadas.** Depois de reportar, o
 *    medidor fica mudo por `cooldownMs` (30 min): o episódio medido durou 2 h,
 *    e 1 relato por minuto seria o próprio BUG-37 (log afogando o alerta).
 * 3. **Nunca é política.** Nada aqui muda o comportamento da sonda — a guarda
 *    candidata do BUG-243 ("exigir `hidden` real antes de aceitar `visible`")
 *    conflita com o roteiro do BUG-196 e precisa de decisão; este é o dado
 *    para tomá-la.
 */

export type ResumeSource = "visibility" | "app";

export interface ResumeBurstReport {
  windowSec: number;
  visible: number;
  appActive: number;
  /** Quanto a página ficou escondida antes de cada `visible` da janela (ms). */
  hiddenMs: number[];
  hiddenMsMin: number | null;
  hiddenMsMedian: number | null;
}

export interface ResumeBurstMeter {
  /** A página foi para o fundo (`visibilitychange` → hidden). */
  hidden(nowMs: number): void;
  /** Uma retomada; devolve o relato quando esta completa uma rajada. */
  resumed(source: ResumeSource, nowMs: number): ResumeBurstReport | null;
}

/** Retomadas (qualquer fonte) numa janela para contar como rajada. */
export const RESUME_BURST_THRESHOLD = 4;
export const RESUME_BURST_WINDOW_MS = 60_000;
export const RESUME_BURST_COOLDOWN_MS = 30 * 60_000;

export function createResumeBurstMeter(opts?: {
  threshold?: number;
  windowMs?: number;
  cooldownMs?: number;
}): ResumeBurstMeter {
  const threshold = opts?.threshold ?? RESUME_BURST_THRESHOLD;
  const windowMs = opts?.windowMs ?? RESUME_BURST_WINDOW_MS;
  const cooldownMs = opts?.cooldownMs ?? RESUME_BURST_COOLDOWN_MS;

  let events: { source: ResumeSource; at: number; hiddenMs: number | null }[] = [];
  let hiddenAt: number | null = null;
  let mutedUntil = -Infinity;

  return {
    hidden(nowMs) {
      hiddenAt = nowMs;
    },
    resumed(source, nowMs) {
      // Só o `visible` fecha um "escondido": o `appStateChange` do nativo chega
      // junto do mesmo retorno e contaria o intervalo duas vezes.
      let hiddenMs: number | null = null;
      if (source === "visibility" && hiddenAt !== null) {
        hiddenMs = Math.max(0, nowMs - hiddenAt);
        hiddenAt = null;
      }
      events = events.filter((e) => nowMs - e.at < windowMs);
      events.push({ source, at: nowMs, hiddenMs });

      if (nowMs < mutedUntil || events.length < threshold) return null;

      mutedUntil = nowMs + cooldownMs;
      const hidden = events.map((e) => e.hiddenMs).filter((v): v is number => v !== null);
      const sorted = [...hidden].sort((a, b) => a - b);
      const report: ResumeBurstReport = {
        windowSec: Math.round(windowMs / 1000),
        visible: events.filter((e) => e.source === "visibility").length,
        appActive: events.filter((e) => e.source === "app").length,
        hiddenMs: hidden,
        hiddenMsMin: sorted.length ? sorted[0] : null,
        hiddenMsMedian: sorted.length ? sorted[Math.floor((sorted.length - 1) / 2)] : null,
      };
      events = [];
      return report;
    },
  };
}
